import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type Step = 'idle' | 'sending' | 'sent' | 'verifying' | 'error';

export function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<Step>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setStep('sending');
    setErrorMsg(null);

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      setStep('error');
      setErrorMsg(error.message);
      return;
    }
    setStep('sent');
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = code.replace(/\s+/g, '').trim();
    if (!token) return;
    setStep('verifying');
    setErrorMsg(null);

    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });

    if (error || !data.session) {
      setStep('sent');
      setErrorMsg(error?.message ?? 'Código incorrecto o caducado.');
      return;
    }
    navigate('/', { replace: true });
  };

  const reset = () => {
    setStep('idle');
    setCode('');
    setErrorMsg(null);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow-sm ring-1 ring-slate-200">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-slate-900 text-lg font-bold text-white">
            PM
          </div>
          <h1 className="text-xl font-semibold text-slate-900">PM-APPROX</h1>
          <p className="mt-1 text-sm text-slate-500">
            {step === 'sent' || step === 'verifying' ? 'Confirma tu acceso' : 'Acceso por email'}
          </p>
        </div>

        {step === 'sent' || step === 'verifying' ? (
          <div className="space-y-4">
            <div className="rounded-md bg-emerald-50 p-3 text-sm text-emerald-800 ring-1 ring-emerald-200">
              Te hemos enviado un email a <strong>{email}</strong>. Puedes pulsar el
              enlace <em>o</em> introducir el código de 6 dígitos abajo.
            </div>

            <form onSubmit={verify} className="space-y-3">
              <div>
                <label
                  className="mb-1 block text-sm font-medium text-slate-700"
                  htmlFor="code"
                >
                  Código de 6 dígitos
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  pattern="[0-9 ]{6,8}"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  className="input text-center text-lg tracking-[0.4em]"
                />
              </div>
              {errorMsg && (
                <p className="rounded-md bg-rose-50 p-2 text-sm text-rose-800 ring-1 ring-rose-200">
                  {errorMsg}
                </p>
              )}
              <button
                type="submit"
                disabled={step === 'verifying' || code.replace(/\s+/g, '').length < 6}
                className="btn-primary w-full disabled:opacity-50"
              >
                {step === 'verifying' ? 'Verificando…' : 'Entrar con código'}
              </button>
            </form>

            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={send}
                disabled={step === 'verifying'}
                className="cursor-pointer text-slate-500 hover:text-slate-800"
              >
                Reenviar email
              </button>
              <button
                type="button"
                onClick={reset}
                className="cursor-pointer text-slate-500 hover:text-slate-800"
              >
                Cambiar email
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={send} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jesus@katonidas.com"
                className="input"
              />
            </div>
            {errorMsg && (
              <p className="rounded-md bg-rose-50 p-2 text-sm text-rose-800 ring-1 ring-rose-200">
                {errorMsg}
              </p>
            )}
            <button
              type="submit"
              disabled={step === 'sending' || !email}
              className="btn-primary w-full disabled:opacity-50"
            >
              {step === 'sending' ? 'Enviando…' : 'Enviar enlace y código'}
            </button>
            <p className="text-center text-xs text-slate-500">
              Recibirás un email con un enlace mágico <strong>y</strong> un código
              de 6 dígitos. Usa el que prefieras.
            </p>
          </form>
        )}

        <p className="mt-6 text-center text-xs text-slate-400">
          Holding Katonidas — uso interno
        </p>
      </div>
    </div>
  );
}
